import React, { useState, useEffect, useCallback } from 'react'
import { adfSettingsApi } from '../../services/adfSettings'
import type { AdfSetting } from '../../services/adfSettings'
import { Toast } from '../shared/Toast'

export function AdfSettings(): React.ReactElement | null {
  const [setting, setSetting] = useState<AdfSetting | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  // Local form state
  const [formEnabled, setFormEnabled] = useState(false)
  const [formAdfEmail, setFormAdfEmail] = useState('')
  const [formAutoResponse, setFormAutoResponse] = useState(false)
  const [formResponseTemplate, setFormResponseTemplate] = useState('')
  const [formNotificationEmails, setFormNotificationEmails] = useState('')

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type })
  }, [])

  const syncFormState = useCallback((s: AdfSetting) => {
    setFormEnabled(s.enabled)
    setFormAdfEmail(s.adf_email || '')
    setFormAutoResponse(s.auto_response_enabled)
    setFormResponseTemplate(s.response_template || '')
    setFormNotificationEmails((s.notification_emails || []).join(', '))
    setHasChanges(false)
  }, [])

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await adfSettingsApi.show()
      setSetting(data)
      syncFormState(data)
    } catch {
      setError('Failed to load ADF settings. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [syncFormState])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const markChanged = () => setHasChanges(true)

  const parseEmails = (value: string): string[] => {
    return value
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e.length > 0)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const updated = await adfSettingsApi.update({
        enabled: formEnabled,
        adf_email: formAdfEmail || null,
        auto_response_enabled: formAutoResponse,
        response_template: formResponseTemplate || null,
        notification_emails: parseEmails(formNotificationEmails),
      })
      setSetting(updated)
      syncFormState(updated)
      showToast('ADF settings saved', 'success')
    } catch {
      showToast('Failed to save ADF settings', 'error')
    } finally {
      setSaving(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12">
        <div className="flex items-center justify-center gap-3 text-grey">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
          <span className="text-sm">Loading ADF settings...</span>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-sm text-grey mb-4">{error}</p>
        <button onClick={loadSettings} className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90">
          Try Again
        </button>
      </div>
    )
  }

  if (!setting) return null

  return (
    <div className="space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-secondary">ILM / ADF Settings</h1>
          <p className="text-sm text-grey mt-1">Configure Auto Data Format (ADF) lead email settings.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* ADF Email Settings */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-secondary">ADF Configuration</h2>
        </div>
        <div className="p-6 space-y-5">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-secondary">Enable ADF</label>
              <p className="text-xs text-grey mt-0.5">Enable ADF lead email processing.</p>
            </div>
            <button
              onClick={() => { setFormEnabled(!formEnabled); markChanged() }}
              role="switch"
              aria-checked={formEnabled}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 cursor-pointer ${
                formEnabled ? 'bg-primary' : 'bg-gray-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                formEnabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {/* ADF Email */}
          <div>
            <label htmlFor="adf-email" className="block text-sm font-medium text-secondary mb-1.5">ADF Email</label>
            <input
              id="adf-email"
              type="email"
              value={formAdfEmail}
              onChange={(e) => { setFormAdfEmail(e.target.value); markChanged() }}
              placeholder="e.g. leads@dealer.com"
              disabled={!formEnabled}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none disabled:bg-gray-50 disabled:text-gray-400"
            />
            <p className="text-xs text-grey mt-1">The email address where ADF lead data will be sent.</p>
          </div>
        </div>
      </div>

      {/* Auto Response Settings */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-secondary">Auto Response</h2>
        </div>
        <div className="p-6 space-y-5">
          {/* Auto Response Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-secondary">Enable Auto Response</label>
              <p className="text-xs text-grey mt-0.5">Automatically send a response to leads.</p>
            </div>
            <button
              onClick={() => { setFormAutoResponse(!formAutoResponse); markChanged() }}
              role="switch"
              aria-checked={formAutoResponse}
              disabled={!formEnabled}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                formAutoResponse && formEnabled ? 'bg-primary' : 'bg-gray-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                formAutoResponse && formEnabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {/* Response Template - shown only when auto response enabled */}
          {formAutoResponse && formEnabled && (
            <div>
              <label htmlFor="response-template" className="block text-sm font-medium text-secondary mb-1.5">Response Template</label>
              <textarea
                id="response-template"
                value={formResponseTemplate}
                onChange={(e) => { setFormResponseTemplate(e.target.value); markChanged() }}
                placeholder="Enter the auto-response email template..."
                rows={6}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none resize-y"
              />
              <p className="text-xs text-grey mt-1">This template will be sent as an automatic reply to incoming leads.</p>
            </div>
          )}
        </div>
      </div>

      {/* Notification Emails */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-secondary">Notification Emails</h2>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label htmlFor="notification-emails" className="block text-sm font-medium text-secondary mb-1.5">Notification Recipients</label>
            <input
              id="notification-emails"
              type="text"
              value={formNotificationEmails}
              onChange={(e) => { setFormNotificationEmails(e.target.value); markChanged() }}
              placeholder="e.g. manager@dealer.com, sales@dealer.com"
              disabled={!formEnabled}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none disabled:bg-gray-50 disabled:text-gray-400"
            />
            <p className="text-xs text-grey mt-1">Comma-separated list of email addresses to notify when a lead is received.</p>
          </div>
        </div>
      </div>
    </div>
  )
}
